import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

const THEME_KEY = 'destiny_theme';

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(THEME_KEY);
      if (saved) return saved;
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    return 'light';
  });

  const [isSystemTheme, setIsSystemTheme] = useState(() => {
    return !localStorage.getItem(THEME_KEY);
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    if (!isSystemTheme) {
      localStorage.setItem(THEME_KEY, theme);
    }
  }, [theme, isSystemTheme]);

  useEffect(() => {
    if (!isSystemTheme) return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setThemeState(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isSystemTheme]);

  const setTheme = useCallback((newTheme) => {
    setIsSystemTheme(false);
    setThemeState(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setIsSystemTheme(false);
    setThemeState(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const setSystemTheme = useCallback(() => {
    setIsSystemTheme(true);
    localStorage.removeItem(THEME_KEY);
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setThemeState(prefersDark ? 'dark' : 'light');
  }, []);

  const isDark = theme === 'dark';

  return (
    <ThemeContext.Provider value={{
      theme,
      isDark,
      isSystemTheme,
      setTheme,
      toggleTheme,
      setSystemTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
